import type { Schemas } from '@npc/api-client'
import { useState, type ReactNode } from 'react'

import { Button } from '../../components/button'
import { Card } from '../../components/card'
import { T } from '../../copy/text'
import { t, type CopyId } from '../../copy'

/**
 * 左边那张卡：贴进来、我去认一认、从此不用管。
 *
 * 设计稿里第 1 步的深色块是一段静态示例（原型不用真的能贴东西）。
 * 这里把它做成真的输入框，示例原文一个字不动地当 placeholder——
 * PRD §4.1 写着「输入框接受一切格式」，没有输入框这一屏就不成立。
 */

const MISSING_COPY: Record<Schemas['SshParseResult']['missing'][number], CopyId | undefined> = {
  address: 'onboarding.missing.address',
  username: 'onboarding.missing.username',
  secret: 'onboarding.missing.secret',
  // 端口缺就按 22 走，不问——问了也只是让人多答一句「不知道」
  port: undefined,
}

function startErrorText(error: unknown): string | null {
  if (!error) return null
  // 后端已经按 onboarding.error.* 渲染好了 user_message。照原样说，
  // 不要一律换成 error.generic——那会把「钥匙不对」说成「我再试试」。
  if (typeof error === 'string' && error.trim()) return error
  if (error instanceof Error && error.message.trim()) return error.message
  return t('error.generic')
}

export function PasteCard({
  busy,
  missing,
  error,
  initialValue,
  passphrase,
  onPassphraseChange,
  onSubmit,
}: {
  busy: boolean
  missing: Schemas['SshParseResult']['missing']
  error?: unknown
  /** 从详情「重新接一次」带过来的地址。只带地址，不回显密钥。 */
  initialValue?: string
  /**
   * 加密私钥的口令。
   *
   * **只有粘贴的内容里没带口令时才需要它。** sshparse 认得
   * 「私钥密码 / 密钥口令 / passphrase」几种写法——带了就不必再问，
   * 问一遍等于让用户做一件平台已经知道答案的事。
   */
  passphrase?: string
  onPassphraseChange?: (value: string) => void
  onSubmit: (raw: string) => void | boolean | Promise<void | boolean>
}) {
  const [raw, setRaw] = useState(initialValue ?? '')
  const asked = missing.map((item) => MISSING_COPY[item]).filter(Boolean) as CopyId[]
  const errorText = startErrorText(error)

  return (
    <Card padding="lg" className="rounded-npc-card-lg">
      <Step index={1} title={<T id="onboarding.step.paste.title" />} />

      {/*
        原始密码/密钥只从这里进请求体，不进任何缓存。
        `autoComplete="off"` + `spellCheck={false}`：别让浏览器把它记下来，也别标红。
      */}
      <textarea
        value={raw}
        onChange={(event) => setRaw(event.target.value)}
        placeholder={t('onboarding.paste.placeholder')}
        rows={5}
        spellCheck={false}
        autoComplete="off"
        aria-label={t('onboarding.step.paste.title')}
        className="mb-npc-gap-sm block w-full resize-y rounded-npc-card-sm bg-npc-dark px-[15px] py-npc-gap-lg font-npc-mono text-npc-body-sm leading-[1.7] text-npc-green-muted placeholder:text-npc-dark-ink-green"
      />

      {onPassphraseChange ? (
        <>
          <input
            type="password"
            value={passphrase ?? ''}
            onChange={(event) => onPassphraseChange(event.target.value)}
            placeholder={t('onboarding.passphrase.placeholder')}
            autoComplete="off"
            aria-label={t('onboarding.passphrase.placeholder')}
            className="mb-npc-gap-sm block w-full rounded-npc-card-sm bg-npc-dark px-[15px] py-npc-gap-sm font-npc-mono text-npc-body-sm text-npc-green-muted placeholder:text-npc-dark-ink-green"
          />
          {errorText ? (
            <div
              className="mb-npc-gap-sm rounded-npc-code border border-npc-red-border bg-npc-red-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-red-ink"
              data-testid="onboard-start-error"
            >
              {errorText}
            </div>
          ) : null}
        </>
      ) : null}

      {asked.length > 0 ? (
        <div className="mb-npc-gap-sm flex flex-col gap-npc-gap-xs rounded-npc-code border border-npc-amber-border bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink">
          {asked.map((id) => (
            <T key={id} id={id} />
          ))}
        </div>
      ) : null}

      {!onPassphraseChange && errorText ? (
        <div
          className="mb-npc-gap-sm rounded-npc-code border border-npc-red-border bg-npc-red-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-red-ink"
          data-testid="onboard-start-error"
        >
          {errorText}
        </div>
      ) : null}

      <div className="mb-npc-card text-npc-caption leading-[1.6] text-npc-ink-soft">
        <T id="onboarding.paste.note" />
      </div>

      <Button
        variant="primary"
        size="sm"
        disabled={busy || raw.trim().length === 0}
        onClick={() => {
          // `true` = 已经把 start 交出去（成或败都算）。parse 拦下或缺项回 `false`，框留下。
          // start 自己抛错时由 OnboardScreen.submit 吞掉并仍回 `true`，密码不许留在框里。
          void Promise.resolve(onSubmit(raw)).then((sent) => {
            if (sent === true) setRaw('')
          })
        }}
        className="mb-npc-card"
      >
        <T id="onboarding.action.start" />
      </Button>

      <Step index={2} title={<T id="onboarding.step.identify.title" />} />
      <div className="mb-npc-card text-npc-caption leading-[1.7] text-npc-ink-soft">
        <T id="onboarding.step.identify.body" />
      </div>

      <Step index={3} title={<T id="onboarding.step.hands_off.title" />} />
      <div className="text-npc-caption leading-[1.7] text-npc-ink-soft">
        <T id="onboarding.step.hands_off.body" />
      </div>
    </Card>
  )
}

/** 绿色小圆里的序号。设计稿里 20px 圆、11px 数字、Space Grotesk。 */
function Step({ index, title }: { index: number; title: ReactNode }) {
  return (
    <div className="mb-npc-grid-sm flex items-center gap-npc-gap-sm">
      <span className="hosts-step-num flex size-[28px] items-center justify-center rounded-npc-full font-npc-display text-npc-meta font-semibold">
        {index}
      </span>
      <span className="text-npc-body-lg font-semibold">{title}</span>
    </div>
  )
}
